import { getMediaOverlayBorder } from '@components/MediaOverlayIconButton/mediaOverlayIconButton.styles';
import { Box, Stack, alpha } from '@mui/material';
import LangEnum from '../../../../../../../lang/LangEnum';
import { CatalogProductsTestIds } from '../../../../../../../utils/testIds/CatalogProducts.testIds';
import ProductGalleryNavButtons from '../../../../../../products/details/gallery/components/ProductGalleryNavButtons/ProductGalleryNavButtons';
import { useTranslation } from '../../../../../../translation/TranslationContext';
import { useCatalogBannerCarousel } from '../hooks/useCatalogBannerCarousel';

const DOT_SIZE = 4;
const ACTIVE_DOT_WIDTH = 20;

interface CatalogBannerCarouselProps {
  urls: string[];
  alt: string;
  aspectRatio: string;
}

const CatalogBannerCarousel = ({ urls, alt, aspectRatio }: CatalogBannerCarouselProps) => {
  const { translate } = useTranslation();
  const { trackRef, activeIndex, scrollToIndex, goToPrevious, goToNext, handleScroll } =
    useCatalogBannerCarousel(urls.length);

  const hasMultipleSlides = urls.length > 1;

  return (
    <Box sx={{ position: 'relative', overflow: 'hidden' }}>
      <Box
        ref={trackRef}
        onScroll={handleScroll}
        sx={{
          display: 'flex',
          overflowX: 'auto',
          scrollSnapType: 'x mandatory',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
        }}
      >
        {urls.map((url, index) => (
          <Box
            key={`${url}-${index}`}
            component="img"
            src={url}
            alt={alt}
            test-id={CatalogProductsTestIds.BANNER}
            sx={{
              display: 'block',
              flex: '0 0 100%',
              minWidth: 0,
              width: '100%',
              aspectRatio,
              objectFit: 'cover',
              scrollSnapAlign: 'start',
            }}
          />
        ))}
      </Box>

      {hasMultipleSlides && (
        <>
          <ProductGalleryNavButtons onPrevious={goToPrevious} onNext={goToNext} />
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.75}
            useFlexGap
            sx={(theme) => ({
              position: 'absolute',
              bottom: 16,
              left: '50%',
              transform: 'translateX(-50%)',
              px: 1,
              py: 0.75,
              borderRadius: `${theme.shape.borderRadiusFull}px`,
              backgroundColor: alpha(theme.palette.common.black, 0.3),
              border: getMediaOverlayBorder(theme),
              backdropFilter: 'blur(4px)',
            })}
          >
            {urls.map((url, index) => (
              <Box
                key={`${url}-${index}`}
                component="button"
                type="button"
                aria-label={translate(LangEnum.HOMEPAGE_BANNER_GO_TO, { position: index + 1 })}
                aria-current={index === activeIndex ? 'true' : undefined}
                onClick={() => scrollToIndex(index)}
                sx={(theme) => ({
                  width: index === activeIndex ? ACTIVE_DOT_WIDTH : DOT_SIZE,
                  height: DOT_SIZE,
                  p: 0,
                  border: 'none',
                  cursor: 'pointer',
                  borderRadius: `${theme.shape.borderRadiusFull}px`,
                  backgroundColor:
                    index === activeIndex
                      ? theme.palette.common.white
                      : alpha(theme.palette.common.white, 0.6),
                  transition: theme.transitions.create('width'),
                })}
              />
            ))}
          </Stack>
        </>
      )}
    </Box>
  );
};

export default CatalogBannerCarousel;
